import {
  createContext,
  PropsWithChildren,
  useCallback,
  useMemo,
  useRef,
  useState,
} from "react";
import UnsavedChangesConfirmModal from "components/common/UnsavedChangesConfirmModal";
import {
  TAttemptUnsavedChangesConfirmOptions,
  TUnsavedChangesContextValue,
  UnsavedChangesDecision,
} from "./UnsavedChangesContext.types";

type TActiveUnsavedChangesRequest = TAttemptUnsavedChangesConfirmOptions & {
  resolve: (decision: UnsavedChangesDecision) => void;
  promise: Promise<UnsavedChangesDecision>;
};

export const UnsavedChangesContext = createContext<
  TUnsavedChangesContextValue | undefined
>(undefined);

export const UnsavedChangesConfirmProvider = ({
  children,
}: PropsWithChildren) => {
  const activeRequestRef = useRef<TActiveUnsavedChangesRequest | null>(null);

  const [activeRequest, setActiveRequest] =
    useState<TActiveUnsavedChangesRequest | null>(null);

  const clearActiveRequest = useCallback(() => {
    activeRequestRef.current = null;
    setActiveRequest(null);
  }, []);

  const attemptUnsavedChangesConfirm = useCallback(
    (options: TAttemptUnsavedChangesConfirmOptions) => {
      if (activeRequestRef.current) {
        return activeRequestRef.current.promise;
      }

      let resolveRequest!: (decision: UnsavedChangesDecision) => void;

      const promise = new Promise<UnsavedChangesDecision>((resolve) => {
        resolveRequest = resolve;
      });

      const request: TActiveUnsavedChangesRequest = {
        ...options,
        resolve: resolveRequest,
        promise,
      };

      activeRequestRef.current = request;
      setActiveRequest(request);

      return promise;
    },
    [],
  );

  const handleConclude = useCallback(
    async (decision: UnsavedChangesDecision) => {
      const request = activeRequestRef.current;

      if (!request) {
        return;
      }

      try {
        if (decision === UnsavedChangesDecision.Save) {
          await request.onSave?.();
        }

        if (decision === UnsavedChangesDecision.Discard) {
          await request.onDiscardChanges?.();
        }

        if (decision === UnsavedChangesDecision.Cancel) {
          await request.onCancel?.();
        }
      } finally {
        request.resolve(decision);
        clearActiveRequest();
      }
    },
    [clearActiveRequest],
  );

  const value = useMemo<TUnsavedChangesContextValue>(
    () => ({
      attemptUnsavedChangesConfirm,
    }),
    [attemptUnsavedChangesConfirm],
  );

  return (
    <UnsavedChangesContext.Provider value={value}>
      {children}
      <UnsavedChangesConfirmModal
        open={!!activeRequest}
        content={activeRequest?.content}
        isNavigationTrigger={activeRequest?.isNavigationTrigger}
        isSaveProhibited={activeRequest?.isSaveProhibited}
        onCancel={() => handleConclude(UnsavedChangesDecision.Cancel)}
        onDiscard={() => handleConclude(UnsavedChangesDecision.Discard)}
        onSave={() => handleConclude(UnsavedChangesDecision.Save)}
      />
    </UnsavedChangesContext.Provider>
  );
};
